import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Download, MessageSquare, Clock, Phone, AlertCircle } from "lucide-react";

export default function SMSNotificationHistory() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<"all" | "sent" | "delivered" | "failed" | "undelivered" | "pending" | "blocked">("all");
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Fetch SMS history
  const { data: historyData, isLoading: historyLoading } = trpc.admin.smsHistory.getUserSMSHistory.useQuery({
    page,
    pageSize,
    status: statusFilter,
  });

  // Fetch SMS statistics
  const { data: statsData } = trpc.admin.smsHistory.getSMSHistoryStats.useQuery();

  // Export SMS history
  const { data: exportData } = trpc.admin.smsHistory.exportSMSHistory.useQuery({});
  
  const handleExport = () => {
    if (exportData?.csv) {
      const element = document.createElement("a");
      element.setAttribute("href", "data:text/csv;charset=utf-8," + encodeURIComponent(exportData.csv));
      element.setAttribute("download", exportData.filename);
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "sent":
        return "default";
      case "delivered":
        return "default";
      case "failed":
      case "undelivered":
        return "destructive";
      case "blocked":
        return "destructive";
      case "pending":
        return "secondary";
      default:
        return "secondary";
    }
  };

  const getDeliveryStatusBadgeVariant = (status?: string) => {
    switch (status) {
      case "delivered":
        return "default";
      case "failed":
      case "undelivered":
        return "destructive";
      case "pending":
      case "sent":
        return "secondary";
      default:
        return "secondary";
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">SMS Notification History</h1>
          <p className="text-muted-foreground">View all your sent SMS notifications and delivery status</p>
        </div>

        {/* Statistics */}
        {statsData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Sent</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{statsData.totalSent}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Delivered</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{statsData.totalDelivered}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Failed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{statsData.totalFailed}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{statsData.successRate}%</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Status</label>
                <Select value={statusFilter} onValueChange={(value: any) => {
                  setStatusFilter(value);
                  setPage(1);
                }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="undelivered">Undelivered</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 items-end">
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={!historyData?.messages.length || !exportData?.csv}
              >
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SMS Messages List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">SMS Messages</CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : historyData?.messages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No SMS messages found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {historyData?.messages.map((msg: any) => (
                  <div
                    key={msg.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent cursor-pointer transition"
                    onClick={() => {
                      setSelectedMessage(msg);
                      setShowDetail(true);
                    }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono text-sm">{msg.phoneNumber}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {new Date(msg.createdAt).toLocaleString()}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge variant={getStatusBadgeVariant(msg.status)}>
                        {msg.status}
                      </Badge>
                      {msg.deliveryStatus && (
                        <Badge variant={getDeliveryStatusBadgeVariant(msg.deliveryStatus)}>
                          {msg.deliveryStatus}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {historyData && historyData.totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>

                <div className="flex items-center gap-2">
                  <span className="text-sm">
                    Page {page} of {historyData.totalPages}
                  </span>
                </div>

                <Button
                  variant="outline"
                  onClick={() => setPage(Math.min(historyData.totalPages, page + 1))}
                  disabled={page === historyData.totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Message Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>SMS Message Details</DialogTitle>
          </DialogHeader>

          {selectedMessage && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Phone Number</label>
                <p className="text-sm text-muted-foreground font-mono">{selectedMessage.phoneNumber}</p>
              </div>

              <div>
                <label className="text-sm font-medium">Status</label>
                <Badge variant={getStatusBadgeVariant(selectedMessage.status)} className="mt-1">
                  {selectedMessage.status}
                </Badge>
              </div>

              {selectedMessage.deliveryStatus && (
                <div>
                  <label className="text-sm font-medium">Delivery Status</label>
                  <Badge variant={getDeliveryStatusBadgeVariant(selectedMessage.deliveryStatus)} className="mt-1">
                    {selectedMessage.deliveryStatus}
                  </Badge>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Sent Time</label>
                <p className="text-sm text-muted-foreground">
                  {new Date(selectedMessage.createdAt).toLocaleString()}
                </p>
              </div>

              {selectedMessage.deliveredAt && (
                <div>
                  <label className="text-sm font-medium">Delivered Time</label>
                  <p className="text-sm text-muted-foreground">
                    {new Date(selectedMessage.deliveredAt).toLocaleString()}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Cost</label>
                <p className="text-sm text-muted-foreground">{selectedMessage.cost || "0"} C</p>
              </div>

              {selectedMessage.message && (
                <div>
                  <label className="text-sm font-medium">Message</label>
                  <p className="text-sm text-muted-foreground mt-1 p-2 bg-muted rounded">
                    {selectedMessage.message}
                  </p>
                </div>
              )}

              {selectedMessage.errorMessage && (
                <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded">
                  <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900">Error</p>
                    <p className="text-sm text-red-700">{selectedMessage.errorMessage}</p>
                    {selectedMessage.errorCode && (
                      <p className="text-xs text-red-600 mt-1">Code: {selectedMessage.errorCode}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
